import { useNavigate } from 'react-router-dom';
import Container from '../components/Container';
import SectionHeading from '../components/SectionHeading';
import { PRODUCT_CATEGORIES, productImage } from '../lib/products';
import { useSite } from '../../utils/site';

export default function Products() {
  const site = useSite();
  const navigate = useNavigate();

  // A plain <a href="?subject=...#contact"> would force a full page reload
  // (react-router only intercepts its own <Link>) - navigate() keeps this
  // an in-app transition, landing on Contact with the subject pre-filled
  // exactly like the old /contact?subject=... route did.
  const enquireAbout = (productName: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(`?subject=${encodeURIComponent(`Enquiry about ${productName}`)}#contact`);
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="products" className="scroll-mt-20">
      <section className="bg-gradient-to-b from-brand-green-50 to-surface py-16 sm:py-6">
        <Container>
          <SectionHeading
            eyebrow="Our Products"
            title="Farm-fresh, category by category"
            subtitle="Everything below is produced on our own farm. Tap Enquire on any product to send us a quick message about pricing or availability."
            center
          />
        </Container>
      </section>

      <section className="py-16 sm:py-20">
        <Container className="space-y-16">
          {PRODUCT_CATEGORIES.map((category) => (
            <div key={category.id}>
              <div className="mb-6 flex items-center gap-3">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-green-50">
                  <category.icon className="h-5 w-5 text-brand-green-700" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-brand-green-900">{category.title}</h2>
                  <p className="text-sm text-ink-500">{category.tagline}</p>
                </div>
              </div>

              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {category.products.map((product) => (
                  <div
                    key={product.name}
                    className="flex flex-col overflow-hidden rounded-xl border border-brand-green-100 bg-surface shadow-card transition-shadow hover:shadow-popover"
                  >
                    <div className="relative aspect-[4/3] w-full overflow-hidden bg-brand-green-50">
                      <img src={productImage(site, product)} alt={product.name} className="h-full w-full object-cover" loading="lazy" />
                      <div className="absolute left-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/90 shadow-card backdrop-blur">
                        <product.icon className="h-4 w-4 text-brand-gold-700" />
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <h3 className="text-base font-semibold text-brand-green-900">{product.name}</h3>
                      <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-500">{product.description}</p>
                      <a
                        href={`?subject=${encodeURIComponent(`Enquiry about ${product.name}`)}#contact`}
                        onClick={enquireAbout(product.name)}
                        className="mt-5 inline-flex w-fit items-center gap-1.5 rounded-full border border-brand-green-700 px-4 py-2 text-xs font-semibold text-brand-green-700 transition-colors hover:bg-brand-green-50"
                      >
                        Enquire
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Container>
      </section>
    </section>
  );
}
